import { ArrowLeft, BookOpen, Search, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/shared/components/ui/drawer'
import { Input } from '@/shared/components/ui/input'
import { documentationService } from '../services/documentationService'
import type {
  DocumentationItem,
  DocumentationSection,
  SearchResult,
} from '../types/documentation.types'
import { DocumentationContent } from './DocumentationContent'
import { DocumentationNavigation } from './DocumentationNavigation'

interface DocumentationDrawerProps {
  trigger: React.ReactNode
}

export function DocumentationDrawer({ trigger }: DocumentationDrawerProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [selectedSection, setSelectedSection] = useState<string | null>(null)
  const [selectedItem, setSelectedItem] = useState<string | null>(null)
  const [sections, setSections] = useState<DocumentationSection[]>([])
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    new Set()
  )

  useEffect(() => {
    if (open) {
      setSections(documentationService.getAllSections())
      // Start with navigation view - don't auto-select any section/item
    }
  }, [open])

  useEffect(() => {
    if (searchQuery.trim()) {
      const results = documentationService.search(searchQuery)
      setSearchResults(results)
    } else {
      setSearchResults([])
    }
  }, [searchQuery])

  const resetDrawer = () => {
    setSearchQuery('')
    setSearchResults([])
    setSelectedSection(null)
    setSelectedItem(null)
    setCollapsedSections(new Set())
  }

  const handleSearchResultClick = (result: SearchResult) => {
    setSelectedSection(result.sectionId)
    setSelectedItem(result.itemId)
    setSearchQuery('')
    setSearchResults([])
  }

  const toggleSectionCollapse = (sectionId: string) => {
    const newCollapsed = new Set(collapsedSections)
    if (newCollapsed.has(sectionId)) {
      newCollapsed.delete(sectionId)
    } else {
      newCollapsed.add(sectionId)
    }
    setCollapsedSections(newCollapsed)
  }

  const handleItemSelect = (sectionId: string, itemId: string) => {
    setSelectedSection(sectionId)
    setSelectedItem(itemId)
  }

  const handleBackToNav = () => {
    setSelectedSection(null)
    setSelectedItem(null)
  }

  const getCurrentItem = (): DocumentationItem | null => {
    if (!selectedSection || !selectedItem) return null
    return documentationService.getItem(selectedSection, selectedItem) || null
  }

  const getCurrentSection = (): DocumentationSection | null => {
    if (!selectedSection) return null
    return documentationService.getSection(selectedSection) || null
  }

  const renderSearchResults = () => {
    if (searchResults.length === 0 && searchQuery.trim()) {
      return (
        <div className='p-4 text-center text-sm text-muted-foreground'>
          No results found for "{searchQuery}"
        </div>
      )
    }

    return (
      <div className='border-b'>
        {searchResults.map((result, index) => (
          <Button
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
            key={`${result.sectionId}-${result.itemId}-${index}`}
            variant='ghost'
            className='w-full justify-start p-4 h-auto text-left'
            onClick={() => handleSearchResultClick(result)}
          >
            <div className='flex-1 min-w-0'>
              <div className='font-medium text-sm'>{result.title}</div>
              <div className='text-xs text-muted-foreground mt-1 line-clamp-2'>
                {result.snippet}
              </div>
              <Badge variant='outline' className='mt-2 text-xs'>
                {result.matchType === 'title'
                  ? 'Title match'
                  : result.matchType === 'keywords'
                    ? 'Keyword match'
                    : 'Content match'}
              </Badge>
            </div>
          </Button>
        ))}
      </div>
    )
  }

  const showingContent = selectedSection && selectedItem
  const showingSearch = searchQuery.trim() || searchResults.length > 0

  return (
    <Drawer
      open={open}
      onOpenChange={newOpen => {
        setOpen(newOpen)
        if (!newOpen) {
          resetDrawer()
        }
      }}
      direction='right'
    >
      <DrawerTrigger asChild>{trigger}</DrawerTrigger>
      <DrawerContent className='h-screen w-[60vw] min-w-[700px] max-w-[900px] ml-auto fixed inset-y-0 right-0 flex flex-col'>
        <DrawerHeader className='border-b flex-shrink-0 px-6 py-4'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              {showingContent && (
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={handleBackToNav}
                  className='p-1 h-auto mr-2'
                >
                  <ArrowLeft className='h-4 w-4' />
                </Button>
              )}
              <div className='w-6 h-6 bg-blue-100 rounded flex items-center justify-center'>
                <BookOpen className='h-4 w-4 text-blue-600' />
              </div>
              <div>
                <DrawerTitle>Documentation</DrawerTitle>
                <DrawerDescription>
                  {showingContent
                    ? `${getCurrentSection()?.title} > ${getCurrentItem()?.title}`
                    : 'Learn how to integrate and use Coomon'}
                </DrawerDescription>
              </div>
            </div>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => setOpen(false)}
              className='p-1 h-auto'
            >
              <X className='h-4 w-4' />
            </Button>
          </div>
        </DrawerHeader>

        {/* Search Bar */}
        {!showingContent && (
          <div className='px-6 py-4 border-b'>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground' />
              <Input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder='Search documentation...'
                className='pl-10 bg-white'
              />
            </div>
          </div>
        )}

        <div className='flex-1 overflow-hidden'>
          {showingContent ? (
            <DocumentationContent
              item={getCurrentItem()!}
              section={getCurrentSection()!}
            />
          ) : showingSearch ? (
            <div className='h-full overflow-y-auto'>
              {renderSearchResults()}
            </div>
          ) : (
            <DocumentationNavigation
              sections={sections}
              collapsedSections={collapsedSections}
              onToggleSection={toggleSectionCollapse}
              onItemSelect={handleItemSelect}
              selectedSection={selectedSection}
              selectedItem={selectedItem}
            />
          )}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
